import React from 'react';
import Column from '@amzn/meridian/column';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { ReportPlanGroup } from './reportPlanOptions';

export interface ReportPlanSelectorProps {
  groups: ReportPlanGroup[];
  value: string | null;
  onChange: (planId: string) => void;
  loading?: boolean;
  /** Overrides the default "Plan" label (EOS reads better as "Reporting on"). */
  label?: string;
  testId?: string;
}

/**
 * Plan picker for the In-Shift / End-of-Shift reports.
 *
 * Options show the plan type (PDP / SOS), plan id, timestamp and owner. Manual
 * (operator) plans are grouped first, then RCC auto-generated plans, then a
 * separate "Pending approval" group — the ordering lives in reportPlanOptions.
 */
const ReportPlanSelector: React.FC<ReportPlanSelectorProps> = ({
  groups,
  value,
  onChange,
  loading = false,
  label = 'Plan',
  testId = 'report-plan-select',
}) => {
  const hasPlans = groups.some((group) => group.options.length > 0);

  return (
    <Column spacing="300">
      <Text tag="label" type="b400">{label}</Text>
      <Select
        data-testid={testId}
        value={value ?? undefined}
        onChange={onChange}
        width={380}
        size="large"
        disabled={loading || !hasPlans}
        placeholder={loading ? 'Loading plans...' : 'No plans available'}
      >
        {groups.map((group) => (
          <SelectOptionGroup key={group.key} label={group.label}>
            {group.options.map((option) => (
              <SelectOption
                key={option.planId}
                value={option.planId}
                label={option.label}
              />
            ))}
          </SelectOptionGroup>
        ))}
      </Select>
    </Column>
  );
};

export default ReportPlanSelector;
